import React, { useState, useEffect } from 'react';
import { forwardRef } from 'react';
import Grid from '@material-ui/core/Grid';
import MaterialTable from '@material-table/core';
import AddBox from '@material-ui/icons/AddBox';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';
import axios from 'axios';
import Alert from '@material-ui/lab/Alert';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { createTheme } from '@material-ui/core/styles';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ExportCsv, ExportPdf } from '@material-table/exporters';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  onPageChange: null,
};

export const MissingAssets = () => {
  // http://localhost:3000/missingassets?organization=172&location=222&t=cf69f777-6fff-46b7-8d91-b756a09e8b4e
  // http://localhost:3000/missingassets?organization=127&location=161&t=4834120e-8d7b-4bef-a1f0-527f20fa9d2e
  // https://smartcleaning.thingsee.io/#/missingassets?organization=127&location=161&t=4834120e-8d7b-4bef-a1f0-527f20fa9d2e
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get('organization');
  const locId = searchParams.get('location');
  const { token } = useAuth();
  const asseturl = `https://eu-api.empathicbuilding.com/v1/organizations/${orgId}/locations/${locId}/assets?type=info`;
  const sensorurl = `https://eu-api.empathicbuilding.com/v1/organizations/${orgId}/locations/${locId}/sensors?sensor_type=location`;
  const api = axios.create({
    baseURL: 'https://eu-api.empathicbuilding.com/v1',
    headers: { Authorization: `Bearer ${token}` },
  });

  const columns = [
    {
      title: 'Asset',
      field: 'asset_id',
      render: (rowData) => (
        <a
          rel="noopener noreferrer"
          target="_blank"
          href={`https://empathicbuilding.com/eb/${orgId}/${locId}/asset/${rowData.asset_id}`}
        >
          {rowData.name}
        </a>
      ),
    },
    { title: 'Last communication time', field: 'last_communication_time' },
    { title: 'Status', field: 'status' },
  ];
  const [data, setData] = useState([]);
  const [iserror] = useState(false);
  const [errorMessages] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const assets = await api.get(asseturl);
      const sensors = await api.get(sensorurl);
      const assettags = assets.data.filter(
        (asset) => asset.subtype === 'asset'
      );
      let finalList = [];
      let tnow = Date.now();
      let status = '';
      let timediff = 0;

      assettags.forEach((asset) => {
        sensors.data.forEach((sensor) => {
          if (asset.hasOwnProperty('data_providers')) {
            if (asset.data_providers[0] === sensor.id) {
              timediff = tnow - sensor.last_communication_time;
              if (parseInt(timediff) > parseInt(3600000)) {
                status = 'missing';
                finalList.push({
                  asset_id: asset.id,
                  name: asset.name,
                  sensort_id: sensor.id,
                  last_communication_time: new Date(
                    sensor.last_communication_time
                  ).toLocaleString(),
                  status: status,
                });
              } else {
                status = 'active';
              }
            }
          }
        });
      });
      setData(finalList);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const theme = createTheme({
    palette: {
      primary: {
        main: '#4caf50',
      },
      secondary: {
        main: '#ff9100',
      },
    },
  });

  return (
    <MuiThemeProvider theme={theme}>
      <div>
        <Grid container spacing={1} style={{ width: '100%' }}>
          <Grid item xs={3}></Grid>
          <Grid item xs={6}>
            <div>
              {iserror && (
                <Alert severity="error">
                  {errorMessages.map((msg, i) => {
                    return <div key={i}>{msg}</div>;
                  })}
                </Alert>
              )}
            </div>

            <h1>Missing assets</h1>
            <MaterialTable
              title="Missing Assets"
              columns={columns}
              data={data}
              icons={tableIcons}
              options={{
                exportMenu: [
                  {
                    label: 'Export CSV',
                    exportFunc: (cols, datas) =>
                      ExportCsv(cols, datas, 'Reservations_report'),
                  },
                  {
                    label: 'Export PDF',
                    //// You can do whatever you wish in this function. We provide the
                    //// raw table columns and table data for you to modify, if needed.
                    // exportFunc: (cols, datas) => console.log({ cols, datas })
                    exportFunc: (cols, datas) =>
                      ExportPdf(cols, datas, 'Reservations_report'),
                  },
                ],
              }}
            />
          </Grid>
          <Grid item xs={3}></Grid>
        </Grid>
      </div>
    </MuiThemeProvider>
  );
};
export default MissingAssets;
